import { useMemo } from "react";
import { useSelector } from "react-redux";

import { Case, CaseAdmin, AdminCaseList, CasesAdmin } from "pwapi";

import { CaseDetailState } from "reducers/caseDetailReducer";

import { StainsState } from "../reducers/frontLoaderReducers";
import { sortBlocks } from "../utils/blockUtils";
import { sortSlides } from "../utils/slideUtils";
import { sortSpecimens } from "../utils/specimenUtils";

type AnyCaseWithSlides = Case | CaseAdmin | AdminCaseList | CaseDetailState;
type AnyCase = AnyCaseWithSlides | CasesAdmin;

export const useSortedSpecimens = (pathCase?: AnyCase) => {
  return useMemo(
    () => sortSpecimens(pathCase?.specimens),
    [pathCase?.specimens]
  );
};

export const useSortedBlocks = (pathCase?: AnyCase) => {
  const sortedSpecimens = useSortedSpecimens(pathCase);
  return useMemo(
    () => sortBlocks(pathCase?.blocks || [], sortedSpecimens),
    [pathCase?.blocks, sortedSpecimens]
  );
};

export const useSortedSlides = (pathCase?: AnyCase) => {
  const sortedSpecimens = useSortedSpecimens(pathCase);
  const sortedBlocks = useSortedBlocks(pathCase);
  const slides = (pathCase as AnyCaseWithSlides)?.slides;
  const stains = useSelector(({ stains }: { stains: StainsState }) => stains);
  return useMemo(
    () => sortSlides(slides || [], sortedBlocks, sortedSpecimens, stains),
    [slides, sortedBlocks, stains]
  );
};

export const useSortedCase = (pathCase?: AnyCase) => {
  const sortedSpecimens = useSortedSpecimens(pathCase);
  const sortedBlocks = useSortedBlocks(pathCase);
  const sortedSlides = useSortedSlides(pathCase);
  return useMemo(
    () => ({
      ...pathCase,
      specimens: sortedSpecimens,
      blocks: sortedBlocks,
      slides: sortedSlides,
    }),
    [sortedSpecimens, sortedBlocks, sortedSlides, pathCase]
  );
};

export default useSortedCase;
